/**
 * cache.ts — the in-memory query cache and its scope-based invalidation bus.
 *
 * ARCHITECTURE §10: "SWR-style hooks with in-memory cache + SSE invalidation". A cache entry
 * lives for the lifetime of the page; SSE `data:changed {scopes}` (or a local mutation) bumps
 * the matching scopes and every mounted `useQuery` on those scopes refetches while keeping the
 * previous render on screen at 50% opacity (no skeleton flash — spec §2.4).
 */

/** The invalidation scopes of the contract's `data:changed` event. */
export type CacheScope =
  | 'summary'
  | 'transactions'
  | 'categories'
  | 'cards'
  | 'networth'
  | 'accounts'
  | 'connections'
  | 'settings'
  | 'alerts'
  | 'users';

interface Entry {
  data: unknown;
  at: number;
}

const entries = new Map<string, Entry>();
const listeners = new Set<(scopes: readonly CacheScope[]) => void>();

export function cacheGet<T>(key: string): T | undefined {
  return entries.get(key)?.data as T | undefined;
}

export function cacheSet(key: string, data: unknown): void {
  entries.set(key, { data, at: Date.now() });
}

export function cacheHas(key: string): boolean {
  return entries.has(key);
}

/** Drop everything (logout). */
export function cacheClear(): void {
  entries.clear();
}

/** Notify every hook subscribed to one of `scopes` that its data is stale. */
export function invalidate(scopes: readonly CacheScope[]): void {
  for (const listener of listeners) listener(scopes);
}

export function subscribeInvalidate(listener: (scopes: readonly CacheScope[]) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Stable cache key from a name and its parameters. */
export function cacheKey(name: string, params?: Record<string, unknown>): string {
  if (!params) return name;
  const parts = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .sort(([x], [y]) => (x < y ? -1 : 1))
    .map(([key, value]) => `${key}=${String(value)}`);
  return parts.length ? `${name}?${parts.join('&')}` : name;
}
